import React, { FunctionComponent, useMemo, useState } from "react";
import { Grid, GridSize, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { IAlbumFormat, useGetAlbumFormats } from "../../../../hooks/albumFormats";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyAlbumFormatDialog } from "../ModifyAlbumFormatDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { AlbumFormatCard } from "../AlbumFormatCard";
import styles from "./styles.module.scss";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useContextMenu } from "../../../../hooks/contextMenu";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const AlbumFormatsDesktop: FunctionComponent<IAlbumFormatsDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onAlbumFormatSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const [contextMenuAlbumFormat, setContextMenuAlbumFormat] = useState<IAlbumFormat>();
  const [albumFormats, resetAlbumFormats, setAlbumFormats, noMoreAlbumFormats] = useGetAlbumFormats(
    isPublic,
    setLoading
  );
  const filteredAlbumFormats = useMemo(() => {
    if (searchText === "") {
      return albumFormats;
    }
    return albumFormats?.filter(albumFormat => {
      return (
        albumFormat.name.toLowerCase().includes(searchText.toLowerCase()) ||
        albumFormat.shortName.toString().toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [albumFormats, searchText]);
  const {
    state: dialogState,
    key: dialogKey,
    openAdd,
    openEdit,
    close: closeDialog
  } = useResettableDialog<IAlbumFormat>();
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredAlbumFormats}
      emptyIconType={attributeIconMap[AttributeType.ALBUM_FORMATS]}
      emptyString={isPublic ? strings.empty_album_formats_public : strings.empty_album_formats}
      addItemString={strings.add_attribute}
      title={strings.attribute_album_formats_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetAlbumFormats}
      onClearValue={onClearValue}
    >
      {filteredAlbumFormats?.map(albumFormat => (
        <Grid item key={albumFormat.uuid} xs={columnWidth}>
          <AlbumFormatCard
            albumFormat={albumFormat}
            onClick={() => {
              if (onAlbumFormatSelected) {
                onAlbumFormatSelected(albumFormat);
              } else {
                openEdit(albumFormat);
              }
            }}
            onContextMenu={event => {
              setContextMenuAlbumFormat(albumFormat);
              handleContextMenu(event);
            }}
            selected={preselectedId === albumFormat.uuid}
          />
        </Grid>
      ))}
      {(loading || !noMoreAlbumFormats) &&
        Array.from({ length: 6 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 64 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextMenuAlbumFormat);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyAlbumFormatDialog
        key={dialogKey}
        open={dialogState.mode !== "closed"}
        onClose={closeDialog}
        albumFormat={dialogState.item}
        onNewItem={newAlbumFormat => {
          const newAlbumFormats = [...albumFormats, newAlbumFormat];
          newAlbumFormats.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setAlbumFormats(newAlbumFormats);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newAlbumFormat => {
          const newAlbumFormats = albumFormats.map(albumFormat =>
            albumFormat.uuid === newAlbumFormat.uuid ? newAlbumFormat : albumFormat
          );
          newAlbumFormats.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setAlbumFormats(newAlbumFormats);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedAlbumFormat => {
          const newAlbumFormats = albumFormats.filter(
            albumFormat => albumFormat.uuid !== deletedAlbumFormat.uuid
          );
          setAlbumFormats(newAlbumFormats);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface IAlbumFormatsDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: GridSize;
  preselectedId?: string;
  onAlbumFormatSelected?: (albumFormat: IAlbumFormat) => void;
  onClearValue?: () => void;
}
